/**
 * BrightPath Family Types
 * 
 * TypeScript interfaces for family-related data structures
 * Used across the family portal for type safety
 */

// =============================================================================
// Core Family Types
// =============================================================================

export interface Family {
  id: string;
  name: string;
  primaryUserId: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country: string;
  settings: FamilySettings;
  hasLegalCase: boolean;
  attorneyId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface FamilySettings {
  timezone: string;
  language: string;
  notifications: {
    email: boolean;
    sms: boolean;
    push: boolean;
  };
  privacy: {
    shareWithAttorney: boolean;
    communityVisible: boolean;
  };
}

export interface FamilyMember {
  id: string;
  familyId: string;
  userId: string;
  relationship: FamilyRelationship;
  isPrimary: boolean;
  permissions: FamilyPermissions;
  joinedAt?: string;
  createdAt: string;
  user: FamilyMemberUser;
}

export interface FamilyMemberUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  avatarUrl?: string;
}

export type FamilyRelationship = 
  | 'parent'
  | 'guardian'
  | 'grandparent'
  | 'sibling'
  | 'caregiver'
  | 'other';

export interface FamilyPermissions {
  canViewHealth: boolean;
  canEditHealth: boolean;
  canViewDocuments: boolean;
  canUploadDocuments: boolean;
  canViewExpenses: boolean;
  canAddExpenses: boolean;
  canManageChildren: boolean;
  canInviteMembers: boolean;
}

// =============================================================================
// Child Types
// =============================================================================

export interface Child {
  id: string;
  familyId: string;
  firstName: string;
  lastName: string;
  nickname?: string;
  dateOfBirth: string;
  gender?: string;
  avatarUrl?: string;
  
  // Birth Information
  birthWeightOz?: number;
  birthLengthInches?: number;
  gestationalAgeWeeks?: number;
  birthHospital?: string;
  birthCity?: string;
  birthState?: string;
  deliveryType?: string;
  
  // Medical Information
  bloodType?: string;
  allergies: string[];
  currentMedications: Medication[];
  
  // Insurance
  insuranceProvider?: string;
  insurancePolicyNumber?: string;
  medicaidId?: string;
  
  // Emergency
  emergencyInfo: EmergencyInfo;
  
  // Metadata
  notes?: string;
  createdAt: string;
  updatedAt: string;
  
  // Related Data (when expanded)
  diagnoses?: ChildDiagnosis[];
}

export interface Medication {
  name: string;
  dosage: string;
  frequency: string;
  prescribedBy?: string;
  startDate?: string;
}

export interface EmergencyInfo {
  primaryContact?: {
    name: string;
    relationship: string;
    phone: string;
  };
  secondaryContact?: {
    name: string;
    relationship: string;
    phone: string;
  };
  hospitalPreference?: string;
  specialInstructions?: string;
}

export interface ChildDiagnosis {
  id: string;
  childId: string;
  diagnosisId: string;
  diagnosedDate?: string;
  diagnosedBy?: string;
  severity?: string;
  subtype?: string;
  notes?: string;
  classificationLevel?: string;
  isPrimary: boolean;
  createdAt: string;
  diagnosis: Diagnosis;
}

export interface Diagnosis {
  id: string;
  slug: string;
  name: string;
  shortName?: string;
  description?: string;
  iconUrl?: string;
}

// =============================================================================
// Dashboard Types
// =============================================================================

export interface DashboardStats {
  therapySessionsThisMonth: number;
  therapySessionsChange: number;
  seizureFreeDays: number;
  seizureFreeDaysNote?: string;
  iepGoalsProgress: number;
  iepGoalsNote?: string;
  ytdExpenses: number;
  ytdExpensesNote?: string;
}

export interface ActivityItem {
  id: string;
  type: ActivityType;
  title: string;
  description?: string;
  timestamp: string;
  relatedEntityId?: string;
  relatedEntityType?: string;
  childId?: string;
  childName?: string;
  metadata?: Record<string, unknown>;
}

export type ActivityType = 
  | 'health_log'
  | 'medication'
  | 'appointment_completed'
  | 'appointment_scheduled'
  | 'document_uploaded'
  | 'expense_added'
  | 'goal_progress'
  | 'goal_achieved'
  | 'care_team_update'
  | 'community_post'
  | 'message_received';

export interface UpcomingAppointment {
  id: string;
  title: string;
  appointmentType?: string;
  providerName?: string;
  locationName?: string;
  locationType: 'in_person' | 'telehealth' | 'phone';
  startsAt: string;
  endsAt: string;
  childId?: string;
  childName?: string;
  status: 'scheduled' | 'confirmed' | 'cancelled';
}

// =============================================================================
// API Request/Response Types
// =============================================================================

export interface CreateFamilyData {
  name: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
}

export interface UpdateFamilyData {
  name?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  settings?: Partial<FamilySettings>;
}

export interface FamilyListResponse {
  families: Family[];
  total: number;
}

export interface FamilyDashboardResponse {
  stats: DashboardStats;
  recentActivity: ActivityItem[];
  upcomingAppointments: UpcomingAppointment[];
  children: Child[];
}

export interface InviteFamilyMemberData {
  email: string;
  relationship: FamilyRelationship;
  permissions: Partial<FamilyPermissions>;
  message?: string;
}

// =============================================================================
// Helper Types
// =============================================================================

export interface FamilyWithMembers extends Family {
  members: FamilyMember[];
}

export interface FamilyWithChildren extends Family {
  children: Child[];
}

export interface ChildWithDiagnoses extends Child {
  diagnoses: ChildDiagnosis[];
}

// Computed helper for age calculation
export function calculateAge(dateOfBirth: string): { years: number; months: number } {
  const birth = new Date(dateOfBirth);
  const now = new Date();
  
  let years = now.getFullYear() - birth.getFullYear();
  let months = now.getMonth() - birth.getMonth();
  
  if (months < 0) {
    years--;
    months += 12;
  }
  
  if (now.getDate() < birth.getDate()) {
    months--;
    if (months < 0) {
      years--;
      months += 12;
    }
  }
  
  return { years, months };
}

export function formatAge(dateOfBirth: string): string {
  const { years, months } = calculateAge(dateOfBirth);
  
  if (years === 0) {
    return `${months} month${months !== 1 ? 's' : ''}`;
  }
  
  if (months === 0) {
    return `${years} year${years !== 1 ? 's' : ''}`;
  }
  
  return `${years}y ${months}m`;
}

// Additional settings-related types and utilities

export type MemberRole = 'admin' | 'parent' | 'caregiver' | 'viewer';

export interface EditMemberFormData {
  role: MemberRole;
  permissions: MemberPermissions;
}

export interface MemberPermissions {
  canViewHealth: boolean;
  canEditHealth: boolean;
  canViewDocuments: boolean;
  canEditDocuments: boolean;
  canViewFinancials: boolean;
  canEditFinancials: boolean;
  canManageTeam: boolean;
  canInviteMembers: boolean;
}

export interface InviteMemberFormData {
  email: string;
  role: MemberRole;
  message?: string;
}

export interface FamilyProfile {
  id: string;
  name: string;
  timezone: string;
  primaryContact?: string;
}

export interface FamilyProfileFormData {
  name: string;
  timezone: string;
  primaryContact?: string;
}

export interface NotificationPreferences {
  emailNotifications: boolean;
  pushNotifications: boolean;
  smsNotifications: boolean;
  healthAlerts: boolean;
  appointmentReminders: boolean;
  medicationReminders: boolean;
  weeklyDigest: boolean;
}

export interface DataExportRequest {
  format: ExportFormat;
  dataTypes: ExportDataType[];
  dateRange?: { start: string; end: string };
}

export type ExportFormat = 'json' | 'csv' | 'pdf';
export type ExportDataType = 'health' | 'documents' | 'appointments' | 'medications' | 'all';

export interface FamilyInvitation {
  id: string;
  email: string;
  role: MemberRole;
  status: 'pending' | 'accepted' | 'expired';
  invitedAt: string;
  expiresAt: string;
}

export type FamilyRole = MemberRole;

export const DEFAULT_PERMISSIONS: MemberPermissions = {
  canViewHealth: true,
  canEditHealth: false,
  canViewDocuments: true,
  canEditDocuments: false,
  canViewFinancials: false,
  canEditFinancials: false,
  canManageTeam: false,
  canInviteMembers: false,
};

export function getRoleLabel(role: MemberRole): string {
  const labels: Record<MemberRole, string> = {
    admin: 'Administrator',
    parent: 'Parent/Guardian',
    caregiver: 'Caregiver',
    viewer: 'View Only',
  };
  return labels[role] || role;
}

export function getRoleDescription(role: MemberRole): string {
  const descriptions: Record<MemberRole, string> = {
    admin: 'Full access to all features and settings',
    parent: 'Can manage health records, documents, and team',
    caregiver: 'Can view and update health records',
    viewer: 'Can only view information',
  };
  return descriptions[role] || '';
}

export function getRoleDefaultPermissions(role: MemberRole): MemberPermissions {
  switch (role) {
    case 'admin':
      return {
        canViewHealth: true,
        canEditHealth: true,
        canViewDocuments: true,
        canEditDocuments: true,
        canViewFinancials: true,
        canEditFinancials: true,
        canManageTeam: true,
        canInviteMembers: true,
      };
    case 'parent':
      return {
        canViewHealth: true,
        canEditHealth: true,
        canViewDocuments: true,
        canEditDocuments: true,
        canViewFinancials: true,
        canEditFinancials: false,
        canManageTeam: true,
        canInviteMembers: true,
      };
    case 'caregiver':
      return {
        canViewHealth: true,
        canEditHealth: true,
        canViewDocuments: true,
        canEditDocuments: false,
        canViewFinancials: false,
        canEditFinancials: false,
        canManageTeam: false,
        canInviteMembers: false,
      };
    default:
      return DEFAULT_PERMISSIONS;
  }
}

// Zod schemas - import zod at the top of your consuming files
import { z } from 'zod';

export const editMemberSchema = z.object({
  role: z.enum(['admin', 'parent', 'caregiver', 'viewer']),
  permissions: z.object({
    canViewHealth: z.boolean(),
    canEditHealth: z.boolean(),
    canViewDocuments: z.boolean(),
    canEditDocuments: z.boolean(),
    canViewFinancials: z.boolean(),
    canEditFinancials: z.boolean(),
    canManageTeam: z.boolean(),
    canInviteMembers: z.boolean(),
  }),
});

export const inviteMemberSchema = z.object({
  email: z.string().email('Invalid email address'),
  role: z.enum(['admin', 'parent', 'caregiver', 'viewer']),
  message: z.string().optional(),
});

export const familyProfileSchema = z.object({
  name: z.string().min(1, 'Family name is required'),
  timezone: z.string(),
  primaryContact: z.string().optional(),
});

export type MemberStatus = 'active' | 'pending' | 'inactive' | 'suspended';

export function getMemberStatusLabel(status: MemberStatus): string {
  const labels: Record<MemberStatus, string> = {
    active: 'Active',
    pending: 'Pending',
    inactive: 'Inactive',
    suspended: 'Suspended',
  };
  return labels[status] || status;
}

export function getMemberStatusColor(status: MemberStatus): string {
  const colors: Record<MemberStatus, string> = {
    active: 'text-green-600 bg-green-100',
    pending: 'text-yellow-600 bg-yellow-100',
    inactive: 'text-gray-600 bg-gray-100',
    suspended: 'text-red-600 bg-red-100',
  };
  return colors[status] || 'text-gray-600 bg-gray-100';
}
